import { useMemo, useRef } from 'react';

interface EmbeddedClaim<T> {
  /**
   * Returns the embedded data for `key`: the part `take` consumes the first
   * time, the very same data again for an identical `key` afterwards.
   */
  claim(key: string, take: () => T | undefined): T | undefined;
  /** Forget the claim (the caller is about to fetch for real). */
  release(): void;
}

/**
 * Bridges a one-shot embedded part (`utils/ssrBootstrap`) and an effect that
 * StrictMode runs twice. The first run consumes the part and applies it; the
 * second run (same `key`, same mounted component) must see the same data, not
 * an already-consumed `undefined` that would turn into a duplicate fetch. The
 * claim lives in a ref of the component, so it dies with it, and `release()`
 * on every fetching run means a later run with a different (or refreshed) key
 * can never reuse old data.
 */
export function useEmbeddedClaim<T>(): EmbeddedClaim<T> {
  const ref = useRef<{ key: string; data: T } | null>(null);
  return useMemo<EmbeddedClaim<T>>(
    () => ({
      claim(key, take) {
        if (ref.current?.key === key) return ref.current.data;
        const data = take();
        ref.current = data === undefined ? null : { key, data };
        return data;
      },
      release() {
        ref.current = null;
      },
    }),
    []
  );
}
